"use client";

import {useTranslation} from "react-i18next";
import Icon from "@/components/library/Icon";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {useSyncCollapsibleOpen} from "@/hooks/useSyncCollapsibleOpen";
import {cn} from "@/lib/utils";

type ToolActivityGroupProps = Readonly<{
   toolCount: number;
   messageCount: number;
   isActive: boolean;
   summaryOverride?: string;
   children: React.ReactNode;
}>;

export default function ToolActivityGroup({
   toolCount,
   messageCount,
   isActive,
   summaryOverride,
   children,
}: ToolActivityGroupProps) {
   const {t} = useTranslation("ask");
   const [open, setOpen] = useSyncCollapsibleOpen(isActive);

   const toolLabel = t("tools.activity.toolCalls", {count: toolCount});
   const summary =
      summaryOverride ??
      (messageCount > 0
         ? t("tools.activity.summaryWithMessages", {
              toolCalls: toolLabel,
              messages: t("tools.activity.messages", {count: messageCount}),
           })
         : toolLabel);

   return (
      <Collapsible open={open} onOpenChange={setOpen} className="font-sans">
         <CollapsibleTrigger className="group -ml-1.5 flex min-h-7 max-w-full items-center gap-2 rounded-md px-1.5 text-left text-[13px] leading-5 text-500 transition-colors hover:bg-neutral/30 hover:text-700">
            <Icon
               icon="chevron-right"
               className={cn(
                  "h-2.5 w-2.5 flex-shrink-0 text-300 transition-transform duration-150",
                  open && "rotate-90"
               )}
            />
            <span className={cn("truncate", isActive && "animate-text-shimmer font-medium text-600")}>
               {summary}
            </span>
         </CollapsibleTrigger>

         <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0">
            <div className="ml-[3px] mt-2 space-y-2.5 border-l border-neutral/70 pl-4">
               {children}
            </div>
         </CollapsibleContent>
      </Collapsible>
   );
}
